import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CCard, CCardGroup } from "@coreui/react";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import SubscriptionPlanForm from "../subscriptionPlanForm";
import { subscriptionPlan } from "@/constants/translation/subscriptionPlan.translation";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import SubscriptionPlanValidationClass from "@/helpers/validations/subscriptionPlan";
import { masters } from "@/libs/routes";
import { useAddSubscriptionPlan } from "@/hooks/useSubscriptionPlan";

/**
 * Component for adding a new subscription plan.
 * @returns {JSX.Element} The rendered AddSubscriptionPlan component.
 */
const AddSubscriptionPlan = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate: doAddSubscriptionPlan } = useAddSubscriptionPlan(
    (response) => {
      toast.success(response?.message || t(messages.toast.subscriptionPlan.added));
      navigate(masters.subscriptionPlan.list);
    },
    (error) => {
      toast.error(error?.response?.data?.message || t(messages.toast.subscriptionPlan.addFailed));
      setIsSubmitting(false);
    }
  );

  const handleAddSubscriptionPlan = (values) => {
    setIsSubmitting(true);
    doAddSubscriptionPlan({
      name: values.name,
      user_type: values.user_type,
      duration: values.duration,
      ...(values.apple_product_id?.trim() ? { apple_product_id: values.apple_product_id.trim() } : {}),
      ...(values.google_product_id?.trim() ? { google_product_id: values.google_product_id.trim() } : {}),
    });
  };

  const initialValues = useMemo(
    () => ({
      name: "",
      user_type: "",
      duration: "",
      apple_product_id: "",
      google_product_id: "",
    }),
    []
  );

  const validationSchema = useMemo(() => SubscriptionPlanValidationClass.subscriptionPlanValidation(t), [t]);

  return (
    <CCardGroup>
      <CCard className="p-4">
        <h1 className="text-center">{t(subscriptionPlan.add.main_heading)}</h1>
        <SubscriptionPlanForm
          initialValues={initialValues}
          onSubmit={handleAddSubscriptionPlan}
          isSubmitting={isSubmitting}
          validationSchema={validationSchema}
        />
      </CCard>
    </CCardGroup>
  );
};

AddSubscriptionPlan.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.subscriptionPlan?.list,
    pathname: masters.subscriptionPlan.list,
    active: false,
  },
  {
    name: breadcrumb_key.masters?.subscriptionPlan?.add,
    pathname: masters.subscriptionPlan.add,
    active: true,
  },
];

export default AddSubscriptionPlan;
